import React, { useMemo, useState } from "react";
import moment from "moment";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

interface Props {
  visitors: any[];
}

const VisitorStatsDashboard: React.FC<Props> = ({ visitors }) => {
  const [timeFilter, setTimeFilter] = useState("all"); // 'all', 'month', 'week', 'day'

  const filteredVisitors = useMemo(() => {
    const now = moment();
    if (timeFilter === "all") {
      return visitors;
    }
    return visitors.filter((visitor) => {
      const createdAt = moment(visitor.createdAt);
      if (timeFilter === "month") {
        return createdAt.isSame(now, "month");
      }
      if (timeFilter === "week") {
        return createdAt.isSame(now, "week");
      }
      if (timeFilter === "day") {
        return createdAt.isSame(now, "day");
      }
      return true;
    });
  }, [visitors, timeFilter]);

  const stats = useMemo(() => {
    const totalVisitors = filteredVisitors.length;
    const registeredVisitors = filteredVisitors.filter(
      (v) => v.registration_statut,
    ).length;
    const interestedVisitors = filteredVisitors.filter((v) =>
      ["Intéressé", "Très Intéressé"].includes(v.level_of_interest),
    ).length;

    const conversionRate =
      totalVisitors > 0 ? (registeredVisitors / totalVisitors) * 100 : 0;

    const now = moment();
    const newToday = visitors.filter((v) =>
      moment(v.createdAt).isSame(now, "day"),
    ).length;
    const newThisWeek = visitors.filter((v) =>
      moment(v.createdAt).isSame(now, "week"),
    ).length;
    const newThisMonth = visitors.filter((v) =>
      moment(v.createdAt).isSame(now, "month"),
    ).length;

    const engagedRate =
      totalVisitors > 0 ? (interestedVisitors / totalVisitors) * 100 : 0;

    return {
      totalVisitors,
      registeredVisitors,
      interestedVisitors,
      conversionRate,
      newToday,
      newThisWeek,
      newThisMonth,
      engagedRate,
    };
  }, [filteredVisitors, visitors]);

  // Group visitors by month for the line chart
  const monthlyData = useMemo(() => {
    const months = moment.monthsShort();
    const counts = new Array(12).fill(0);

    visitors.forEach((v) => {
      const month = moment(v.createdAt).month();
      counts[month]++;
    });

    return {
      labels: months,
      data: counts,
    };
  }, [visitors]);

  const lineChartOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        id: "visitors-line",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      colors: ["#0d6efd"],
      xaxis: {
        categories: monthlyData.labels,
      },
      title: {
        text: "Évolution des Visiteurs",
        align: "left",
        style: { fontSize: "16px", fontWeight: "bold" },
      },
      markers: {
        size: 4,
      },
      grid: {
        borderColor: "#f1f1f1",
      },
    }),
    [monthlyData.labels],
  );

  const lineChartSeries = useMemo(
    () => [
      {
        name: "Visiteurs",
        data: monthlyData.data,
      },
    ],
    [monthlyData.data],
  );

  const barChartOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        id: "new-visitors-bar",
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          horizontal: false,
          columnWidth: "55%",
        },
      },
      dataLabels: { enabled: false },
      colors: ["#0dcaf0"],
      xaxis: {
        categories: ["Aujourd'hui", "Cette semaine", "Ce mois"],
      },
      title: {
        text: "Nouveaux Visiteurs",
        align: "left",
        style: { fontSize: "16px", fontWeight: "bold" },
      },
    }),
    [],
  );

  const barChartSeries = useMemo(
    () => [
      {
        name: "Nouveaux",
        data: [stats.newToday, stats.newThisWeek, stats.newThisMonth],
      },
    ],
    [stats.newToday, stats.newThisWeek, stats.newThisMonth],
  );

  return (
    <div className="visitor-stats-dashboard mb-4">
      {/* Metric Cards */}
      <div className="row g-3">
        <div className="col-lg-3 col-md-6">
          <div className="card shadow-sm border-0 h-100 bg-primary-transparent border-start border-primary border-4">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="text-muted mb-0">Total Visiteurs</h6>
                <i className="ti ti-users text-primary fs-20"></i>
              </div>
              <h3 className="fw-bold mb-0 text-dark">{stats.totalVisitors}</h3>
              <div className="mt-2">
                <span className="badge bg-white text-primary rounded-pill border border-primary">
                  {timeFilter === "all" ? "Cumulé" : `Filtre: ${timeFilter}`}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6">
          <div className="card shadow-sm border-0 h-100 bg-success-transparent border-start border-success border-4">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="text-muted mb-0">Inscrits</h6>
                <i className="ti ti-user-check text-success fs-20"></i>
              </div>
              <h3 className="fw-bold mb-0 text-dark">
                {stats.registeredVisitors}
              </h3>
              <div className="mt-2 d-flex align-items-center">
                <span className="text-success small fw-bold me-1">
                  {stats.conversionRate.toFixed(1)}%
                </span>
                <span className="text-muted small">conversion</span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6">
          <div className="card shadow-sm border-0 h-100 bg-info-transparent border-start border-info border-4">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="text-muted mb-0">Engagement</h6>
                <i className="ti ti-thumb-up text-info fs-20"></i>
              </div>
              <h3 className="fw-bold mb-0 text-dark">
                {stats.engagedRate.toFixed(1)}%
              </h3>
              <div className="mt-2">
                <div className="progress progress-xs" style={{ height: "6px" }}>
                  <div
                    className="progress-bar bg-info"
                    style={{ width: `${stats.engagedRate}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6">
          <div className="card shadow-sm border-0 h-100 bg-warning-transparent border-start border-warning border-4">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="text-muted mb-0">Nouveaux (Mois)</h6>
                <i className="ti ti-user-plus text-warning fs-20"></i>
              </div>
              <h3 className="fw-bold mb-0 text-dark">{stats.newThisMonth}</h3>
              <p className="text-muted small mb-0 mt-2">
                {stats.newToday} aujourd'hui
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="row g-3 mt-3">
        <div className="col-lg-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <Chart
                options={lineChartOptions}
                series={lineChartSeries}
                type="line"
                height={300}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body d-flex flex-column justify-content-center">
              <Chart
                options={barChartOptions}
                series={barChartSeries}
                type="bar"
                height={300}
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .bg-primary-transparent { background: rgba(13, 110, 253, 0.05) !important; }
        .bg-success-transparent { background: rgba(25, 135, 84, 0.05) !important; }
        .bg-info-transparent { background: rgba(13, 202, 240, 0.05) !important; }
        .bg-warning-transparent { background: rgba(255, 193, 7, 0.05) !important; }
      `}</style>
    </div>
  );
};

export default VisitorStatsDashboard;
